import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient, ApiClientError } from '@/api/client'
import { localDatabase } from '@/db/localDatabase'
import type { LoginRequest, RegisterRequest, LoginResponse, ApiUser, UpdateProfileRequest } from '@/types/api'
import { authLogger } from '@/utils/logger'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<ApiUser | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const expiresAt = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let refreshInterval: ReturnType<typeof setInterval> | null = null

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !isTokenExpired())

  function isTokenExpired(): boolean {
    if (!expiresAt.value) return true
    return Date.now() >= expiresAt.value
  }

  // Actions
  function initialize(): void {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedExpiresAt = localStorage.getItem('expires_at')
    const storedUser = localStorage.getItem('user')

    if (storedAccessToken && storedRefreshToken && storedExpiresAt) {
      accessToken.value = storedAccessToken
      refreshToken.value = storedRefreshToken
      expiresAt.value = parseInt(storedExpiresAt, 10)
      apiClient.setAccessToken(storedAccessToken)

      if (storedUser) {
        try {
          user.value = JSON.parse(storedUser)
        } catch {
          user.value = null
        }
      }

      // Check if token is expired and try to refresh
      if (isTokenExpired() && refreshToken.value) {
        refreshTokens()
      } else {
        // Fetch fresh profile data on app startup
        fetchProfile()
      }
    }
  }

  function saveAuth(response: LoginResponse): void {
    authLogger.debug('Saving auth data:', {
      userId: response.user_id,
      username: response.username,
      expiresIn: response.expires_in,
      hasAccessToken: !!response.access_token,
      hasRefreshToken: !!response.refresh_token
    })

    accessToken.value = response.access_token
    refreshToken.value = response.refresh_token
    expiresAt.value = Date.now() + response.expires_in * 1000
    user.value = {
      id: response.user_id,
      username: response.username,
      email: user.value?.email,
      full_name: user.value?.full_name,
      has_password: user.value?.has_password,
      auth_methods: user.value?.auth_methods,
      email_confirmed: user.value?.email_confirmed
    }

    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('refresh_token', response.refresh_token)
    localStorage.setItem('expires_at', expiresAt.value.toString())
    localStorage.setItem('user', JSON.stringify(user.value))

    apiClient.setAccessToken(response.access_token)
  }

  async function fetchProfile(): Promise<void> {
    try {
      const profile = await apiClient.getProfile()
      authLogger.debug('Profile fetched:', profile)
      if (user.value) {
        user.value = {
          ...user.value,
          email: profile.email,
          full_name: profile.full_name,
          has_password: profile.has_password,
          auth_methods: profile.auth_methods,
          email_confirmed: profile.email_confirmed
        }
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (e) {
      authLogger.error('Failed to fetch profile:', e)
    }
  }

  function clearAuth(): void {
    accessToken.value = null
    refreshToken.value = null
    expiresAt.value = null
    user.value = null

    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('user')

    apiClient.setAccessToken(null)
    clearTokenRefresh()
  }

  async function login(data: LoginRequest): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.login(data)
      saveAuth(response)
      await fetchProfile()
      // Initialize local database with routes from server
      await initializeRoutesAfterLogin()
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function register(data: RegisterRequest): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      await apiClient.register(data)
      // Don't auto-login - user needs to confirm email via OTP first
      authLogger.debug('Registration successful, user needs to confirm email')
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function refreshTokens(): Promise<boolean> {
    if (!refreshToken.value) {
      clearAuth()
      return false
    }

    try {
      const response = await apiClient.refresh({
        refresh_token: refreshToken.value
      })
      saveAuth(response)
      return true
    } catch {
      clearAuth()
      return false
    }
  }

  async function initializeRoutesAfterLogin(): Promise<void> {
    authLogger.debug('Fetching routes from server')
    try {
      const response = await apiClient.listRoutes()
      authLogger.debug('Routes fetched, count:', response.routes?.length || 0)
      await localDatabase.saveRoutes(response.routes)
      authLogger.debug('Routes saved to local database')
    } catch (e) {
      authLogger.error('Failed to initialize routes after login:', e)
      // Don't throw - login was successful, route initialization is secondary
    }
  }

  async function logout(): Promise<void> {
    isLoading.value = true

    try {
      if (refreshToken.value) {
        await apiClient.logout({
          refresh_token: refreshToken.value
        })
      }
    } catch {
      // Ignore logout errors
    } finally {
      // Clear local database
      try {
        await localDatabase.clearAllData()
      } catch (e) {
        authLogger.error('Failed to clear local database:', e)
        // Continue with logout even if database clear fails
      }
      clearAuth()
      isLoading.value = false
    }
  }

  async function sendOtp(email: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      await apiClient.sendOtp({ email: email })
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function loginWithOtp(email: string, otpCode: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.otpLogin({
        email: email,
        otp_code: otpCode
      })
      saveAuth(response)
      await fetchProfile()
      // Initialize local database with routes from server
      await initializeRoutesAfterLogin()
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function loginWithGoogle(idToken: string): Promise<void> {
    authLogger.debug('Starting Google login')
    isLoading.value = true
    error.value = null

    try {
      authLogger.debug('Calling Google login API')
      const response = await apiClient.googleLogin({
        id_token: idToken
      })
      authLogger.debug('Google login successful, saving auth')
      saveAuth(response as LoginResponse)
      await fetchProfile()
      // Initialize local database with routes from server
      authLogger.debug('Initializing routes after login')
      await initializeRoutesAfterLogin()
      authLogger.debug('Google login complete')
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  // Auto-refresh token before expiration
  function setupTokenRefresh(): void {
    clearTokenRefresh()
    refreshInterval = setInterval(async () => {
      if (isAuthenticated.value && expiresAt.value) {
        // Refresh 1 minute before expiration
        const refreshThreshold = expiresAt.value - 60000
        if (Date.now() >= refreshThreshold) {
          await refreshTokens()
        }
      }
    }, 30000) // Check every 30 seconds
  }

  function clearTokenRefresh(): void {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  async function updateProfile(data: UpdateProfileRequest): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.updateProfile(data)
      // Update user data in store
      if (user.value) {
        user.value = {
          ...user.value,
          full_name: response.full_name,
          has_password: response.has_password,
          auth_methods: response.auth_methods
        }
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (e) {
      if (e instanceof ApiClientError) {
        error.value = e.message
      } else {
        error.value = 'An unexpected error occurred'
      }
      throw e
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    user,
    accessToken,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    // Actions
    initialize,
    login,
    register,
    logout,
    refreshTokens,
    sendOtp,
    loginWithOtp,
    loginWithGoogle,
    setupTokenRefresh,
    fetchProfile,
    updateProfile
  }
})
